import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import {
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import { ChartResultStatus } from 'charts/catalog/types';
import { INSPECTION_SUMMARY_PARTS } from 'charts/components/triplet/inspectionSummaryParts';
import {
  buildChartExportFileName,
  type ChartExportFormat,
  exportChartCsv,
  exportChartPdf,
  exportChartPng,
  exportChartXlsx,
} from 'charts/export/exporters';
import { buildChartTable } from 'charts/export/chartTable';
import { useCatalogChartSource } from 'charts/hooks/useCatalogChartData';
import { buildChartData } from 'charts/model/buildChartData';
import { CHART_FONT_FAMILY } from 'charts/theme/muiChartSlotProps';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys, CommonTranslationKeys } from 'intl/keys';
import {
  useCallback,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { showToast } from 'services/toaster';
import { logError } from 'setup/logger/logError';
import type { TranslationKey } from 'intl/types/translationKey';
import type { ChartExportMenuProps } from './types';

export type { ChartExportMenuProps } from './types';

const EXPORT_OPTIONS: readonly Readonly<{
  format: ChartExportFormat;
  labelId: TranslationKey;
  icon: ReactNode;
}>[] = [
  {
    format: EXTENSIONS.PNG,
    labelId: AnalyticsTranslationKeys.exportPng,
    icon: <ImageOutlinedIcon fontSize='small' />,
  },
  {
    format: EXTENSIONS.PDF,
    labelId: AnalyticsTranslationKeys.exportPdf,
    icon: <PictureAsPdfOutlinedIcon fontSize='small' />,
  },
  {
    format: EXTENSIONS.CSV,
    labelId: AnalyticsTranslationKeys.exportCsv,
    icon: <TextSnippetOutlinedIcon fontSize='small' />,
  },
  {
    format: EXTENSIONS.XLSX,
    labelId: AnalyticsTranslationKeys.exportXlsx,
    icon: <TableChartOutlinedIcon fontSize='small' />,
  },
];

const exportButtonSx = {
  textTransform: 'capitalize',
  height: 'fit-content',
  // Match the 24px black refresh icon next to it.
  '& .MuiButton-startIcon > *:nth-of-type(1)': {
    fontSize: 24,
    color: 'common.black',
  },
} as const;

/** Export of a chart to PNG, PDF, CSV or XLSX: in its details and on its dashboard tile (PT-6804). */
export function ChartExportMenu({
  chartId,
  title,
  snapshotRef,
  variant = 'button',
}: ChartExportMenuProps): ReactElement {
  const intl = useIntl();
  const theme = useTheme();
  // The data is built only when exporting: every dashboard tile has this menu.
  const { chart, result, definition, ctx } = useCatalogChartSource(chartId);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const isReady =
    result?.status === ChartResultStatus.success &&
    Boolean(definition) &&
    !chart?.viewModel.isEmpty;

  const openMenu = useCallback((event: MouseEvent<HTMLElement>): void => {
    // On a tile the menu must not open the chart details.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const runExport = useCallback(
    async (format: ChartExportFormat): Promise<void> => {
      const node = snapshotRef.current;
      if (!result || !definition || !node) {
        return;
      }

      const now = new Date();
      const fileName = buildChartExportFileName(title, now);
      const snapshotOptions = {
        backgroundColor: theme.palette.background.paper,
        footerColor: theme.palette.text.secondary,
        fontFamily: CHART_FONT_FAMILY,
      };

      switch (format) {
        case EXTENSIONS.PNG:
          return exportChartPng(node, fileName, snapshotOptions);
        case EXTENSIONS.PDF:
          return exportChartPdf(node, fileName, {
            ...snapshotOptions,
            footer: formatTranslation(
              intl,
              AnalyticsTranslationKeys.exportGeneratedAt,
              { date: intl.formatDate(now, { dateStyle: 'medium', timeStyle: 'short' }) }
            ),
          });
        case EXTENSIONS.CSV:
        case EXTENSIONS.XLSX: {
          const table = buildChartTable(
            chart?.viewModel.type ?? definition.type,
            buildChartData(definition, result, ctx),
            {
              category: formatTranslation(
                intl,
                AnalyticsTranslationKeys.exportColumnCategory
              ),
              value: formatTranslation(
                intl,
                AnalyticsTranslationKeys.exportColumnValue
              ),
              triplet: INSPECTION_SUMMARY_PARTS.map(({ titleId }) =>
                formatTranslation(intl, titleId)
              ),
            }
          );
          return format === EXTENSIONS.CSV
            ? exportChartCsv(table, fileName)
            : exportChartXlsx(table, title, fileName);
        }
      }
    },
    [chart, ctx, definition, intl, result, snapshotRef, theme, title]
  );

  const handleExport = useCallback(
    async (format: ChartExportFormat): Promise<void> => {
      closeMenu();
      setIsExporting(true);
      try {
        await runExport(format);
      } catch (error) {
        logError('Chart export failed', error as object | null | undefined);
        showToast({
          message: { id: AnalyticsTranslationKeys.exportError },
          type: TOASTER_TYPES.FAILURE,
          toasterPosition: { vertical: 'bottom', horizontal: 'left' },
        });
      } finally {
        setIsExporting(false);
      }
    },
    [closeMenu, runExport]
  );

  const exportLabel = formatTranslation(intl, CommonTranslationKeys.export);
  const exportIcon = isExporting ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />;
  const triggerProps = {
    disabled: !isReady || isExporting,
    'data-qa': 'export-chart',
    'aria-haspopup': 'menu',
    'aria-expanded': Boolean(anchorEl),
    onClick: openMenu,
  } as const;

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={exportLabel}>
          <span>
            <IconButton size='small' aria-label={exportLabel} {...triggerProps}>
              {exportIcon}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button startIcon={exportIcon} sx={exportButtonSx} {...triggerProps}>
          {exportLabel}
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onClick={(event): void => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {EXPORT_OPTIONS.map(({ format, labelId, icon }) => (
          <MenuItem
            key={format}
            data-qa={`export-chart-${format}`}
            onClick={(): void => {
              void handleExport(format);
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{formatTranslation(intl, labelId)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
